import { useState, useEffect } from 'react';
import { api } from '../services/api';
import { Bell, Plus, Edit2, Trash2, X, AlertCircle, CheckCircle, Power, Send } from 'lucide-react';

interface Broadcast {
  id: string;
  title: string;
  message: string;
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  expiresAt: string | null;
  creator: {
    id: string;
    name: string;
    email: string;
  };
}

export default function BroadcastsPage() {
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);
  const [resendingBroadcast, setResendingBroadcast] = useState<Broadcast | null>(null);
  const [sendToAll, setSendToAll] = useState(false);
  const [editingBroadcast, setEditingBroadcast] = useState<Broadcast | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    title: '',
    message: '',
    priority: 'NORMAL' as Broadcast['priority'],
    expiresAt: '',
  });

  useEffect(() => {
    loadBroadcasts();
  }, []);

  const loadBroadcasts = async () => {
    try {
      setIsLoading(true);
      const response = await api.getBroadcasts();
      console.log('Broadcasts response:', response);
      setBroadcasts(response.broadcasts || []);
    } catch (error: any) {
      console.error('Error loading broadcasts:', error);
      console.error('Error details:', error.response?.data);
      setError(error.response?.data?.error || 'Erro ao carregar avisos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      if (editingBroadcast) {
        await api.updateBroadcast(editingBroadcast.id, {
          ...formData,
          isActive: editingBroadcast.isActive,
        });
        setSuccess('Aviso atualizado com sucesso!');
      } else {
        await api.createBroadcast(formData);
        setSuccess('Aviso criado com sucesso!');
      }

      await loadBroadcasts();
      handleCloseModal();
    } catch (error: any) {
      console.error('Error saving broadcast:', error);
      setError(error.response?.data?.error || 'Erro ao salvar aviso');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este aviso?')) return;

    try {
      await api.deleteBroadcast(id);
      setSuccess('Aviso excluído com sucesso!');
      await loadBroadcasts();
    } catch (error: any) {
      console.error('Error deleting broadcast:', error);
      setError(error.response?.data?.error || 'Erro ao excluir aviso');
    }
  };

  const handleToggleStatus = async (broadcast: Broadcast) => {
    try {
      await api.toggleBroadcastStatus(broadcast.id, !broadcast.isActive);
      setSuccess(`Aviso ${!broadcast.isActive ? 'ativado' : 'desativado'} com sucesso!`);
      await loadBroadcasts();
    } catch (error: any) {
      console.error('Error toggling broadcast status:', error);
      setError(error.response?.data?.error || 'Erro ao alterar status do aviso');
    }
  };

  const handleResendClick = (broadcast: Broadcast) => {
    setResendingBroadcast(broadcast);
    setSendToAll(false);
    setShowResendModal(true);
  };

  const handleConfirmResend = async () => {
    if (!resendingBroadcast) return;

    try {
      await api.resendBroadcast(resendingBroadcast.id, sendToAll);
      const message = sendToAll
        ? 'Aviso reenviado com sucesso! Todos os usuários (online e offline) receberão a notificação.'
        : 'Aviso reenviado com sucesso! Todos os usuários conectados receberão a notificação.';
      setSuccess(message);
      setShowResendModal(false);
      setResendingBroadcast(null);
      setSendToAll(false);
      await loadBroadcasts();
    } catch (error: any) {
      console.error('Error resending broadcast:', error);
      setError(error.response?.data?.error || 'Erro ao reenviar aviso');
    }
  };

  const handleEdit = (broadcast: Broadcast) => {
    setEditingBroadcast(broadcast);
    setFormData({
      title: broadcast.title,
      message: broadcast.message,
      priority: broadcast.priority,
      expiresAt: broadcast.expiresAt ? new Date(broadcast.expiresAt).toISOString().slice(0, 16) : '',
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingBroadcast(null);
    setFormData({
      title: '',
      message: '',
      priority: 'NORMAL',
      expiresAt: '',
    });
  };

  const getPriorityColor = (priority: Broadcast['priority']) => {
    switch (priority) {
      case 'URGENT':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'HIGH':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      case 'NORMAL':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'LOW':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400';
    }
  };

  const getPriorityLabel = (priority: Broadcast['priority']) => {
    switch (priority) {
      case 'URGENT':
        return 'Urgente';
      case 'HIGH':
        return 'Alta';
      case 'NORMAL':
        return 'Normal';
      case 'LOW':
        return 'Baixa';
      default:
        return priority;
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Avisos do Sistema</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Gerencie avisos transmitidos em tempo real para todos os usuários
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Novo Aviso
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
          <span className="text-red-800 dark:text-red-400">{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
          <span className="text-green-800 dark:text-green-400">{success}</span>
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 dark:text-gray-400 mt-4">Carregando avisos...</p>
        </div>
      ) : broadcasts.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <Bell className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Nenhum aviso cadastrado</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {broadcasts.map((broadcast) => (
            <div
              key={broadcast.id}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {broadcast.title}
                    </h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(broadcast.priority)}`}>
                      {getPriorityLabel(broadcast.priority)}
                    </span>
                    {broadcast.isActive ? (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                        Ativo
                      </span>
                    ) : (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400">
                        Inativo
                      </span>
                    )}
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap mb-3">
                    {broadcast.message}
                  </p>
                  <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                    <p>Criado por: {broadcast.creator.name} em {new Date(broadcast.createdAt).toLocaleString('pt-BR')}</p>
                    {broadcast.expiresAt && (
                      <p>Expira em: {new Date(broadcast.expiresAt).toLocaleString('pt-BR')}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <button
                    onClick={() => handleResendClick(broadcast)}
                    className="p-2 bg-purple-100 dark:bg-purple-900/30 hover:bg-purple-200 dark:hover:bg-purple-900/50 text-purple-700 dark:text-purple-400 rounded-lg transition"
                    title="Reenviar para todos os usuários"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleToggleStatus(broadcast)}
                    className={`p-2 rounded-lg transition ${
                      broadcast.isActive
                        ? 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                        : 'bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/50 text-green-700 dark:text-green-400'
                    }`}
                    title={broadcast.isActive ? 'Desativar' : 'Ativar'}
                  >
                    <Power className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleEdit(broadcast)}
                    className="p-2 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-400 rounded-lg transition"
                    title="Editar"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(broadcast.id)}
                    className="p-2 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-700 dark:text-red-400 rounded-lg transition"
                    title="Excluir"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingBroadcast ? 'Editar Aviso' : 'Novo Aviso'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Título *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  required
                  maxLength={100}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Mensagem *
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 min-h-[120px]"
                  required
                  maxLength={500}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {formData.message.length}/500 caracteres
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Prioridade
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value as Broadcast['priority'] })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="LOW">Baixa</option>
                  <option value="NORMAL">Normal</option>
                  <option value="HIGH">Alta</option>
                  <option value="URGENT">Urgente</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Data de Expiração (Opcional)
                </label>
                <input
                  type="datetime-local"
                  value={formData.expiresAt}
                  onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Deixe em branco para aviso sem data de expiração
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Salvando...' : editingBroadcast ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Resend Confirmation Modal */}
      {showResendModal && resendingBroadcast && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              Reenviar Aviso
            </h3>
            <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-lg mb-4">
              <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                {resendingBroadcast.title}
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                {resendingBroadcast.message}
              </p>
            </div>

            <div className="mb-6">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Selecione para quem enviar:
              </p>
              <div className="space-y-2">
                <label className="flex items-start p-3 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                  <input
                    type="radio"
                    name="sendType"
                    checked={!sendToAll}
                    onChange={() => setSendToAll(false)}
                    className="mt-1 mr-3"
                  />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Apenas usuários online</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Usuários que dispensaram o aviso verão novamente</p>
                  </div>
                </label>
                <label className="flex items-start p-3 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                  <input
                    type="radio"
                    name="sendType"
                    checked={sendToAll}
                    onChange={() => setSendToAll(true)}
                    className="mt-1 mr-3"
                  />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Todos os usuários</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Incluindo usuários offline (verão ao fazer login)</p>
                  </div>
                </label>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowResendModal(false);
                  setResendingBroadcast(null);
                  setSendToAll(false);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmResend}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2"
              >
                <Send className="w-4 h-4" />
                Reenviar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
